import type { CertificadoInfo, ConfiguracaoEquipe, Estado, ResumoAgente } from '@contefy/shared';
import { ESTADO_NOME, ESTADOS } from '@contefy/shared';
import { useEffect, useRef, useState } from 'react';
import { LimiteErro } from '../componentes/LimiteErro';
import { Alerta, Botao, Cartao, Titulo } from '../componentes/ui';
import { equipeApi } from './api';

/** Regras editáveis pelo administrador (guardadas na tabela configuracoes). */
export function Configuracoes() {
  const [lista, setLista] = useState<ConfiguracaoEquipe[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const carregar = () =>
    equipeApi
      .configuracoes()
      .then(setLista)
      .catch((e) => setErro((e as Error).message));
  useEffect(() => {
    carregar();
  }, []);
  if (erro) return <Alerta tipo="erro">{erro}</Alerta>;
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Configurações</h1>
        <p className="text-sm text-slate-600">
          Regras do módulo. Valores em JSON: texto entre aspas, números, listas entre colchetes.
        </p>
      </div>
      <LimiteErro nome="Certificado digital">
        <Certificado />
      </LimiteErro>
      <LimiteErro nome="Mensagens de assinatura">
        <MensagensAssinatura />
      </LimiteErro>
      <LimiteErro nome="Agentes">
        <Agentes />
      </LimiteErro>
      <div className="pt-4">
        <h2 className="text-lg font-semibold text-slate-900">Regras</h2>
      </div>
      {lista?.map((c) => (
        <Item key={c.chave} c={c} aoSalvar={carregar} />
      ))}
    </div>
  );
}

/** Texto de assinatura de cada Junta. O painel escolhe pelo Estado da empresa; a equipe só põe o link ou o número. */
function MensagensAssinatura() {
  const [textos, setTextos] = useState<Record<Estado, string> | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    equipeApi
      .modelosAssinatura()
      .then(setTextos)
      .catch((e) => setMsg((e as Error).message));
  }, []);
  const salvar = async () => {
    if (!textos) return;
    setSalvando(true);
    setMsg(null);
    try {
      setTextos(await equipeApi.salvarModelosAssinatura(textos));
      setMsg('Salvo.');
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <section className="space-y-3 pt-2">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Mensagens de assinatura por Junta</h2>
        <p className="text-sm text-slate-600">
          Na aba Mensagens de cada processo, o texto "Assinar o contrato" sai no modelo da Junta do
          Estado da empresa. Marcadores: <code>{'{nome}'}</code> (sócio), <code>{'{empresa}'}</code>
          , <code>{'{link}'}</code> (link de assinatura, PR) e <code>{'{processo}'}</code> (número
          do processo, MG/SC). O que a equipe não informar vira lacuna entre colchetes.
        </p>
      </div>
      {!textos && !msg && <p className="text-sm text-slate-500">Carregando…</p>}
      {textos && (
        <Cartao className="space-y-3">
          {ESTADOS.map((uf) => (
            <label key={uf} className="block text-sm">
              <span className="font-medium text-slate-800">{ESTADO_NOME[uf]}</span>
              <textarea
                className="mt-1 min-h-32 w-full rounded-lg border border-slate-300 p-2 text-sm"
                value={textos[uf]}
                onChange={(e) => setTextos({ ...textos, [uf]: e.target.value })}
              />
            </label>
          ))}
          <div className="flex items-center gap-3">
            <Botao carregando={salvando} onClick={salvar}>
              Salvar mensagens
            </Botao>
            {msg && <span className="text-sm text-slate-600">{msg}</span>}
          </div>
        </Cartao>
      )}
    </section>
  );
}

const ESTADO_AGENTE: Record<ResumoAgente['estado'], { rotulo: string; cor: string }> = {
  ativo: { rotulo: 'Ativo', cor: 'bg-emerald-100 text-emerald-800' },
  preparacao: { rotulo: 'Em preparação', cor: 'bg-amber-100 text-amber-800' },
  planejado: { rotulo: 'Planejado', cor: 'bg-slate-100 text-slate-600' },
};

/** Os agentes especialistas: quem são, o que fazem, o que exigem aprovação humana e como andaram. */
function Agentes() {
  const [agentes, setAgentes] = useState<ResumoAgente[] | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  useEffect(() => {
    equipeApi
      .agentes()
      .then(setAgentes)
      .catch(() => setAgentes([]));
  }, []);
  return (
    <section className="space-y-3 pt-2">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Agentes especialistas</h2>
        <p className="text-sm text-slate-600">
          Cada agente tem uma missão, ferramentas próprias e pontos em que só avança com aprovação
          da equipe. Tudo que fazem fica registrado na aba Agentes de cada abertura.
        </p>
      </div>
      {agentes === null && <p className="text-sm text-slate-500">Carregando…</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {agentes?.map((a) => (
          <Cartao key={a.id} className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">{a.nome}</div>
                <div className="text-xs text-slate-500">
                  {a.etapas.map((e) => e).join(', ')}
                  {a.estados ? ` · ${a.estados.join(', ')}` : ''} · modelo: {a.modelo}
                </div>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${ESTADO_AGENTE[a.estado].cor}`}
              >
                {ESTADO_AGENTE[a.estado].rotulo}
              </span>
            </div>
            <p className="text-sm text-slate-700">{a.papel}</p>
            <div className="flex flex-wrap gap-3 text-xs text-slate-600">
              <span>
                7 dias: <b>{a.execucoes_7d}</b> execuções
              </span>
              {a.falhas_7d > 0 && <span className="text-red-700">{a.falhas_7d} falhas</span>}
              {a.aguardando_humano > 0 && (
                <span className="text-amber-700">{a.aguardando_humano} aguardando equipe</span>
              )}
              {a.ultima_execucao && (
                <span>última: {new Date(a.ultima_execucao).toLocaleString('pt-BR')}</span>
              )}
            </div>
            {a.checkpoints.length > 0 && (
              <div className="text-xs text-slate-600">
                <b>Só com aprovação humana:</b> {a.checkpoints.join('; ')}
              </div>
            )}
            <button
              type="button"
              className="text-xs font-medium text-marca-700 hover:underline"
              onClick={() => setAberto(aberto === a.id ? null : a.id)}
            >
              {aberto === a.id ? 'Ocultar missão' : 'Ver missão e ferramentas'}
            </button>
            {aberto === a.id && (
              <div className="space-y-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                <pre className="whitespace-pre-wrap font-sans">{a.missao}</pre>
                <div>
                  <b>Ferramentas:</b> {a.ferramentas.join(', ')}
                </div>
                {a.conhecimento.length > 0 && (
                  <div>
                    <b>Conhecimento:</b> {a.conhecimento.join(', ')}
                  </div>
                )}
              </div>
            )}
          </Cartao>
        ))}
      </div>
    </section>
  );
}

function Item({ c, aoSalvar }: { c: ConfiguracaoEquipe; aoSalvar: () => void }) {
  const [texto, setTexto] = useState(
    JSON.stringify(c.valor, null, Array.isArray(c.valor) || typeof c.valor === 'object' ? 2 : 0),
  );
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const salvar = async () => {
    setSalvando(true);
    setMsg(null);
    try {
      const valor = JSON.parse(texto);
      await equipeApi.salvarConfiguracao(c.chave, valor);
      setMsg('Salvo.');
      aoSalvar();
    } catch (e) {
      setMsg(e instanceof SyntaxError ? 'JSON inválido.' : (e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Cartao>
      <Titulo sub={c.descricao ?? undefined}>
        <code className="text-base">{c.chave}</code>
      </Titulo>
      <textarea
        className="font-mono text-sm"
        rows={Math.min(12, Math.max(2, texto.split('\n').length))}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
      />
      <div className="mt-2 flex items-center gap-3">
        <Botao className="!min-h-9 !py-1.5" carregando={salvando} onClick={salvar}>
          Salvar
        </Botao>
        {msg && (
          <span className={`text-sm ${msg === 'Salvo.' ? 'text-emerald-700' : 'text-red-700'}`}>
            {msg}
          </span>
        )}
      </div>
    </Cartao>
  );
}

/** Certificado A1 do responsável: enviado aqui, guardado cifrado no servidor. A senha nunca volta. */
function Certificado() {
  const [info, setInfo] = useState<CertificadoInfo | null | undefined>(undefined);
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);
  const carregar = () =>
    equipeApi
      .certificado()
      .then(setInfo)
      .catch(() => setInfo(null));
  useEffect(() => {
    carregar();
  }, []);

  const enviar = async () => {
    const arquivo = arquivoRef.current?.files?.[0];
    setErro(null);
    setOk(null);
    if (!arquivo) return setErro('Escolha o arquivo .pfx ou .p12.');
    if (!senha) return setErro('Informe a senha do certificado.');
    setEnviando(true);
    try {
      const r = await equipeApi.enviarCertificado(arquivo, senha);
      setInfo(r);
      setSenha('');
      if (arquivoRef.current) arquivoRef.current.value = '';
      setOk(
        `Certificado de ${r.titular} válido até ${new Date(r.valido_ate).toLocaleDateString('pt-BR')}.`,
      );
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  const remover = async () => {
    if (
      !window.confirm(
        'Remover o certificado do servidor? O agente da Junta deixa de conseguir entrar no gov.br.',
      )
    )
      return;
    await equipeApi.removerCertificado();
    setInfo(null);
  };

  return (
    <section className="space-y-3 pt-2">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Certificado digital A1</h2>
        <p className="text-sm text-slate-600">
          Usado pelo Agente da Junta para entrar no gov.br e assinar como contador. O arquivo fica
          cifrado no servidor e a senha não é exibida de volta. Quando renovar o certificado, envie
          o novo aqui.
        </p>
      </div>
      <Cartao className="space-y-3">
        {info === undefined && <p className="text-sm text-slate-500">Carregando…</p>}
        {info === null && (
          <p className="text-sm text-slate-700">Nenhum certificado enviado ainda.</p>
        )}
        {info && (
          <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            <div>
              <b>{info.titular}</b>
              {info.cpf_cnpj && <span className="text-slate-500"> · {info.cpf_cnpj}</span>}
            </div>
            <div>
              Emitido por {info.emissor} · válido até{' '}
              <b className={info.dias_para_vencer < 30 ? 'text-red-700' : ''}>
                {new Date(info.valido_ate).toLocaleDateString('pt-BR')}
              </b>{' '}
              ({info.dias_para_vencer} dias)
            </div>
            <div className="text-xs text-slate-500">
              Arquivo {info.nome_arquivo}, enviado em{' '}
              {new Date(info.enviado_em).toLocaleString('pt-BR')} por {info.enviado_por}
            </div>
            <div className="mt-2 text-xs text-slate-600">
              <b>Usos recentes pelo sistema:</b>{' '}
              {(info.usos_recentes ?? []).length === 0
                ? 'nenhum ainda.'
                : (info.usos_recentes ?? [])
                    .map(
                      (u) =>
                        `${new Date(u.quando).toLocaleString('pt-BR')} · ${u.finalidade} (${u.quem})`,
                    )
                    .join(' · ')}
            </div>
            <div className="mt-2 text-xs text-slate-600">
              <b>Login do gov.br guardado:</b>{' '}
              {info.sessao_govbr ? (
                <>
                  salvo em {new Date(info.sessao_govbr.salva_em).toLocaleString('pt-BR')} por{' '}
                  {info.sessao_govbr.salva_por}
                  {info.sessao_govbr.expira_em &&
                    ` · vale até ${new Date(info.sessao_govbr.expira_em).toLocaleString('pt-BR')}`}
                  . O agente entra sem captcha enquanto valer.{' '}
                  <button
                    type="button"
                    className="underline"
                    onClick={async () => {
                      if (
                        !window.confirm(
                          'Esquecer o login guardado? Na próxima rodada alguém terá de resolver o captcha de novo.',
                        )
                      )
                        return;
                      await equipeApi.esquecerSessaoGovbr();
                      setInfo({ ...info, sessao_govbr: null });
                    }}
                  >
                    Esquecer
                  </button>
                </>
              ) : (
                'nenhum. Na primeira rodada do agente, alguém resolve o captcha pelo painel (aba Agentes → Assumir navegador) e o login fica guardado.'
              )}
            </div>
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <label className="text-sm">
            <span className="text-slate-600">Arquivo (.pfx ou .p12)</span>
            <input
              ref={arquivoRef}
              type="file"
              accept=".pfx,.p12"
              className="mt-1 w-full text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-600">Senha</span>
            <input
              type="password"
              className="mt-1 w-full"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="off"
            />
          </label>
          <Botao carregando={enviando} onClick={enviar}>
            {info ? 'Substituir' : 'Enviar'}
          </Botao>
        </div>
        {erro && <Alerta tipo="erro">{erro}</Alerta>}
        {ok && <Alerta tipo="sucesso">{ok}</Alerta>}
        {info && (
          <button type="button" className="text-xs text-red-700 hover:underline" onClick={remover}>
            Remover certificado do servidor
          </button>
        )}
      </Cartao>
    </section>
  );
}
